import {
  CreateActivationChannelMutationVariables,
  ActivationChannelInput,
  Engagements,
  CreateActivationChannelMutation,
  ChannelType,
  ActivationChannel,
  UpdateActivationChannelMutation,
  UpdateActivationChannelMutationVariables,
  UpdateActivationChannelInput,
  TestS3ActivationChannelMutation,
  TestS3ActivationChannelMutationVariables,
  TestGoogleActivationChannelMutation,
  TestGoogleActivationChannelMutationVariables,
} from './../../../../API';
import {
  CreateActivationChannel,
  TestS3ActivationChannel,
  TestGoogleActivationChannel,
  UpdateActivationChannel,
} from './../../../../graphql/mutations';
import { ApiService } from './../../../shared/services/api.service';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import {
  CreateActivationChannelForm,
  GetUserAccessTokenResponse,
  TypeDropdown,
  ValidateUserTokenResponse,
} from '../../models/query-interface';
import { ConnectionStatus } from '../../../../API';
import {
  AT_LEAST_ONE_ALPHABET,
  NO_WHITESPACE_ALLOWED,
  NOT_ALLOWED_SPECIAL_CHARS,
  ONLY_NUMBERS,
} from '../../../shared/utils/constants';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AppService } from '../../../shared/services/app.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { EncryptionService } from '../../../shared/services/encryption.service';
import { FacebookService } from '../../services/facebook.service';
import { concatMap, finalize } from 'rxjs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { HttpErrorResponse } from '@angular/common/http';

dayjs.extend(duration);

@Component({
  selector: 'app-create-activation-channel-modal',
  templateUrl: './create-activation-channel-modal.component.html',
  styleUrl: './create-activation-channel-modal.component.scss',
})
export class CreateActivationChannelModalComponent implements OnInit, OnDestroy {
  readonly iconColorCode: string = '#52c41a';
  readonly activationChannel: ActivationChannel = inject(NZ_MODAL_DATA);
  secretVisible: boolean = false;
  developerTokenVisible: boolean = false;
  clientIdVisible: boolean = false;
  tokenVisible: boolean = false;
  isEdit: boolean = false;

  channelId!: string;
  spinningState: 'reset' | 'test' | 'create' | null = null;
  isResetCredentials: boolean = false;
  readonly channelType: TypeDropdown[] = [
    {
      name: 'Meta',
      value: ChannelType.META,
    },
    {
      name: 'S3',
      value: ChannelType.S3,
    },
    {
      name: 'Google',
      value: ChannelType.GOOGLE,
    },
  ];

  activationChannelForm: FormGroup<CreateActivationChannelForm>;

  connectionStatus: ConnectionStatus = ConnectionStatus.NOT_VERIFIED;

  constructor(
    private appService: AppService,
    private fb: FormBuilder,
    private nzMessageService: NzMessageService,
    private userManagementService: UserManagementService,
    private apiService: ApiService,
    private nzModalRef: NzModalRef,
    private encryptionService: EncryptionService,
    private facebookService: FacebookService,
  ) {
    this.activationChannelForm = this.fb.nonNullable.group({
      engagement: ['', Validators.required],
      orgType: ['', Validators.required],
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
        ],
      ],
      description: ['', [Validators.required, Validators.maxLength(500), Validators.pattern(AT_LEAST_ONE_ALPHABET)]],
      channelType: [ChannelType.META, Validators.required],
      createdBy: ['', Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.spinningState = null;
    this.activationChannelForm.reset();
    this.connectionStatus = ConnectionStatus.NOT_VERIFIED;
  }

  ngOnInit(): void {
    this.appService.activeEngagement$.subscribe((engagements: Engagements) => {
      this.activationChannelForm.patchValue({
        engagement: engagements?.engagement,
        orgType: this.userManagementService.getOrgType(),
        createdBy: this.userManagementService.getEmail(),
      });
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.activationChannelForm.get('channelType')?.valueChanges.subscribe((channelType: ChannelType) => {
      if (!this.channelId || this.isResetCredentials) {
        this.activationChannelForm.removeControl('channelSecrets');
        if (this.activationChannelForm.get('bucketName')) {
          this.activationChannelForm.removeControl('bucketName');
        }
        if (this.activationChannelForm.get('awsRegion')) {
          this.activationChannelForm.removeControl('awsRegion');
        }
        if (this.activationChannelForm.get('iamRoleArn')) {
          this.activationChannelForm.removeControl('iamRoleArn');
        }
        this.connectionStatus = ConnectionStatus.NOT_VERIFIED;
        this.addSecretsFormGroup();
      }
    });

    if (this.activationChannel) {
      this.isEdit = true;
      this.channelId = this.activationChannel.channelId;
      this.connectionStatus = this.activationChannel.connectionStatus!;

      this.activationChannelForm.patchValue({
        channelType: this.activationChannel.channelType!,
        description: this.activationChannel.description!,
        name: this.activationChannel.name!,
      });

      if (this.activationChannel.channelType === ChannelType.S3) {
        this.addSecretsFormGroup();

        this.activationChannelForm.patchValue({
          bucketName: this.activationChannel.bucketName || '',
          awsRegion: this.activationChannel.awsRegion || '',
          iamRoleArn: this.activationChannel.iamRoleArn || '',
        });
      } else {
        this.addSecretsFormGroup();
      }
    } else {
      this.addSecretsFormGroup();
      this.isResetCredentials = true;
    }
  }

  addSecretsFormGroup(): void {
    if (this.activationChannelForm.get('channelType')?.value === ChannelType.META) {
      this.activationChannelForm.addControl(
        'channelSecrets',
        this.fb.group({
          appId: ['', [Validators.required, Validators.pattern(ONLY_NUMBERS)]],
          appSecret: ['', [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)]],
          appAccessToken: ['', [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)]],
          adAccountId: ['', [Validators.required, Validators.pattern(ONLY_NUMBERS)]],
        }),
      );
      if (this.channelSecretsFormGroup) {
        this.channelSecretsFormGroup?.valueChanges.subscribe(() => {
          if (this.connectionStatus === ConnectionStatus.SUCCESS) {
            this.connectionStatus = ConnectionStatus.NOT_VERIFIED;
          }
        });
      }
    } else if (this.activationChannelForm.get('channelType')?.value === ChannelType.S3) {
      const arnPattern = /^arn:aws:iam::\d{12}:role\/[\w+=,.@-]+$/;
      const regionPattern = /^[a-z]{2}-[a-z]+-\d$/;

      this.activationChannelForm.addControl(
        'bucketName',
        this.fb.nonNullable.control<string>('', [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)]),
      );
      this.activationChannelForm.addControl(
        'awsRegion',
        this.fb.nonNullable.control<string>('', [
          Validators.required,
          Validators.pattern(NO_WHITESPACE_ALLOWED),
          Validators.pattern(regionPattern),
        ]),
      );
      this.activationChannelForm.addControl(
        'iamRoleArn',
        this.fb.nonNullable.control<string>('', [
          Validators.required,
          Validators.pattern(NO_WHITESPACE_ALLOWED),
          Validators.pattern(arnPattern),
        ]),
      );
    } else if (this.activationChannelForm.get('channelType')?.value === ChannelType.GOOGLE) {
      this.activationChannelForm.addControl(
        'channelSecrets',
        this.fb.group({
          oauthClientId: ['', [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)]],
          oauthClientSecret: ['', [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)]],
          refreshToken: ['', [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)]],
          developerToken: ['', [Validators.required, Validators.pattern(NO_WHITESPACE_ALLOWED)]],
          customerId: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
          loginCustomerId: ['', [Validators.pattern(/^\d{10}$/)]],
        }),
      );
      if (this.channelSecretsFormGroup) {
        this.channelSecretsFormGroup?.valueChanges.subscribe(() => {
          if (this.connectionStatus === ConnectionStatus.SUCCESS) {
            this.connectionStatus = ConnectionStatus.NOT_VERIFIED;
          }
        });
      }
    }
  }

  getUserAccessToken() {
    this.spinningState = 'test';

    if (this.channelTypeFormControl.value === ChannelType.META) {
      this.testMetaCredentials();
    } else if (this.channelTypeFormControl.value === ChannelType.S3) {
      this.testS3Credentials();
    } else if (this.channelTypeFormControl.value === ChannelType.GOOGLE) {
      this.testGoogleCredentials();
    }
  }

  testMetaCredentials() {
    const { appId, appSecret, appAccessToken } = this.channelSecretsFormGroup.value;
    if (appId && appSecret && appAccessToken) {
      this.facebookService
        .getUserAccessToken(appId, appSecret)
        .pipe(
          finalize(() => (this.spinningState = null)),
          concatMap((userAccessTokenResponse: GetUserAccessTokenResponse) => {
            const userAccessToken = userAccessTokenResponse.access_token;
            return this.facebookService.validateUserToken(userAccessToken, appAccessToken);
          }),
        )
        .subscribe({
          next: (response: ValidateUserTokenResponse) => {
            console.log('Meta Credentials Test Response ', response);
            const { data } = response;
            const isValid = data.is_valid;
            if (!isValid) {
              const errorMessage = data.error?.message || 'Invalid credentials';
              this.nzMessageService.warning(`Failed to validate ${errorMessage}`);
              this.connectionStatus = ConnectionStatus.FAIL;
              return;
            }
            const isValidScope = data.scopes.includes('ads_management');
            const expiresAt = data.expires_at ?? 0;
            // Meta returns expires_at = 0 for never-expiring tokens
            const isNeverExpiring = expiresAt === 0;
            const isValidExpiry = isNeverExpiring || dayjs.unix(expiresAt).diff(dayjs(), 'day') >= 30;

            if (isValidScope && isValidExpiry) {
              this.nzMessageService.success('Connection Successful');
              this.connectionStatus = ConnectionStatus.SUCCESS;
            } else if (!isValidScope) {
              this.nzMessageService.warning(
                'Connection is Successful, but scope is invalid. Please update and try again',
              );
              this.connectionStatus = ConnectionStatus.FAIL;
            } else if (!isValidExpiry) {
              this.nzMessageService.warning(
                'Connection is Successful, but expiry is less than 30 days. Please update and try again',
              );
              this.connectionStatus = ConnectionStatus.FAIL;
            }
          },
          error: (error: HttpErrorResponse) => {
            this.connectionStatus = ConnectionStatus.FAIL;
            this.nzMessageService.error(
              `${error.name}! Failed to validate ${this.activationChannelNameFormControl.value} credentials`,
            );
          },
        });
    }
  }

  testS3Credentials() {
    this.apiService
      .query<TestS3ActivationChannelMutation, TestS3ActivationChannelMutationVariables>(TestS3ActivationChannel, {
        testRequest: {
          bucketName: this.bucketNameFormControl.value,
          awsRegion: this.awsRegionFormControl.value,
          iamRoleArn: this.iamRoleArnFormControl.value,
          channelType: this.channelTypeFormControl.value,
        },
      })
      .then((response) => {
        const result = response.data?.TestS3ActivationChannel;
        if (result?.status === 'SUCCESS') {
          this.connectionStatus = ConnectionStatus.SUCCESS;
          this.nzMessageService.success('S3 credentials validated successfully');
        } else if (result?.status === 'ERROR') {
          this.connectionStatus = ConnectionStatus.FAIL;
          console.warn('S3 Test Credentials Failed', result.message);
          this.nzMessageService.error(
            `Failed to establish connection to S3. Please verify your inputs : ${result.message}`,
          );
        }
      })
      .catch((error) => {
        this.connectionStatus = ConnectionStatus.FAIL;
        this.nzMessageService.error(
          `${error.name}! Failed to validate ${this.activationChannelNameFormControl.value} S3 credentials`,
        );
      })
      .finally(() => (this.spinningState = null));
  }

  testGoogleCredentials() {
    const { oauthClientId, oauthClientSecret, refreshToken, developerToken, customerId, loginCustomerId } =
      this.channelSecretsFormGroup.value;

    if (oauthClientId && oauthClientSecret && refreshToken && developerToken && customerId) {
      this.apiService
        .query<TestGoogleActivationChannelMutation, TestGoogleActivationChannelMutationVariables>(
          TestGoogleActivationChannel,
          {
            testRequest: {
              oauthClientId,
              oauthClientSecret,
              refreshToken,
              developerToken,
              customerId,
              loginCustomerId: loginCustomerId || undefined,
              channelType: this.channelTypeFormControl.value,
            },
          },
        )
        .then((response) => {
          const result = response.data?.TestGoogleActivationChannel;
          if (result?.status === 'SUCCESS') {
            this.connectionStatus = ConnectionStatus.SUCCESS;
            this.nzMessageService.success('Google Ads credentials validated successfully');
          } else {
            this.connectionStatus = ConnectionStatus.FAIL;
            this.nzMessageService.error(
              `Failed to establish connection to Google Ads. Please verify your credentials: ${result?.message}`,
            );
          }
        })
        .catch((error) => {
          this.connectionStatus = ConnectionStatus.FAIL;
          this.nzMessageService.error(
            `${error.name}! Failed to validate ${this.activationChannelNameFormControl.value} Google Ads credentials`,
          );
        })
        .finally(() => (this.spinningState = null));
    } else {
      this.connectionStatus = ConnectionStatus.FAIL;
      this.nzMessageService.error('Please fill in all required Google Ads credentials');
      this.spinningState = null;
    }
  }

  handleSave(): void {
    this.spinningState = 'create';
    this.createActivationChannelApi().finally(() => {
      this.spinningState = null;
    });
  }

  handleUpdate(): void {
    this.spinningState = 'create';
    this.updateActivationChannelApi().finally(() => {
      this.spinningState = null;
    });
  }

  createActivationChannelApi(): Promise<void> {
    const { value } = this.activationChannelForm;

    if (value.channelType === ChannelType.META) {
      const { channelSecrets } = value;
      return this.encryptionService
        .encryptSecret(JSON.stringify(channelSecrets))
        .then((encryptedChannelSecrets) => {
          const activationChannelInput: ActivationChannelInput = {
            ...value,
            engagement: value.engagement!,
            lastModifiedBy: this.userManagementService.getEmail(),
            connectionStatus: this.connectionStatus,
            channelSecrets: `"${encryptedChannelSecrets}"`,
          };

          this.apiService
            .query<CreateActivationChannelMutation, CreateActivationChannelMutationVariables>(CreateActivationChannel, {
              activationChannelInput: activationChannelInput,
            })
            .then((result: any) => {
              if (result.errors?.length) {
                this.nzMessageService.error('Failed to create Activation Channel');
                throw new Error('Failed to create Activation Channel');
              }
              this.triggerOk();
              this.nzMessageService.success('Activation Channel Created Successfully');
            })
            .catch((error: any) => {
              this.nzMessageService.error(error?.message ?? 'Failed to create Activation Channel');
            });
        })
        .catch((error: any) => {
          this.nzMessageService.error(error?.message ?? 'Failed to fetch Secrets of Activation Channel');
        });
    } else if (value.channelType === ChannelType.GOOGLE) {
      const { channelSecrets } = value;
      const activationChannelInput: ActivationChannelInput = {
        ...value,
        engagement: value.engagement!,
        lastModifiedBy: this.userManagementService.getEmail(),
        connectionStatus: this.connectionStatus,
        channelSecrets: JSON.stringify(channelSecrets),
      };

      return this.apiService
        .query<CreateActivationChannelMutation, CreateActivationChannelMutationVariables>(CreateActivationChannel, {
          activationChannelInput: activationChannelInput,
        })
        .then((result: any) => {
          if (result.errors?.length) {
            this.nzMessageService.error('Failed to create Activation Channel');
            throw new Error('Failed to create Activation Channel');
          }
          this.triggerOk();
          this.nzMessageService.success('Activation Channel Created Successfully');
        })
        .catch((error: any) => {
          this.nzMessageService.error(error?.message ?? 'Failed to create Activation Channel');
        });
    } else if (value.channelType === ChannelType.S3) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { channelSecrets, ...inputValue } = value;

      const activationChannelInput: ActivationChannelInput = {
        ...inputValue,
        engagement: value.engagement!,
        lastModifiedBy: this.userManagementService.getEmail(),
        connectionStatus: this.connectionStatus,
      };

      return this.apiService
        .query<CreateActivationChannelMutation, CreateActivationChannelMutationVariables>(CreateActivationChannel, {
          activationChannelInput: activationChannelInput,
        })
        .then((result: any) => {
          if (result.errors?.length) {
            this.nzMessageService.error('Failed to create Activation Channel');
            throw new Error('Failed to create Activation Channel');
          }
          this.triggerOk();
          this.nzMessageService.success('Activation Channel Created Successfully');
        })
        .catch((error: any) => {
          this.nzMessageService.error(error?.message ?? 'Failed to create Activation Channel');
        });
    }

    return Promise.reject('Invalid channel type');
  }

  updateActivationChannelApi(): Promise<void> {
    const { value } = this.activationChannelForm;

    if (value.channelType === ChannelType.META) {
      const { channelSecrets } = value;
      if (channelSecrets) {
        return this.encryptionService.encryptSecret(JSON.stringify(channelSecrets)).then((encryptedChannelSecrets) => {
          delete value.createdBy;
          const activationChannelInput: UpdateActivationChannelInput = {
            ...value,
            channelId: this.channelId,
            engagement: value.engagement!,
            lastModifiedBy: this.userManagementService.getEmail(),
            connectionStatus: this.connectionStatus,
            channelSecrets: `"${encryptedChannelSecrets}"`,
          };

          this.apiService
            .query<UpdateActivationChannelMutation, UpdateActivationChannelMutationVariables>(UpdateActivationChannel, {
              activationChannelInput: activationChannelInput,
            })
            .then((result: any) => {
              if (result.errors?.length) {
                this.nzMessageService.error('Failed to update Activation Channel');
                throw new Error('Failed to update Activation Channel');
              }
              this.triggerOk();
              this.nzMessageService.success('Activation Channel Updated Successfully');
            })
            .catch((error: any) => {
              this.nzMessageService.error(error?.message ?? 'Failed to update Activation Channel');
            });
        });
      } else {
        delete value.createdBy;
        const activationChannelInput: UpdateActivationChannelInput = {
          ...value,
          channelId: this.channelId,
          engagement: value.engagement!,
          lastModifiedBy: this.userManagementService.getEmail(),
          connectionStatus: this.connectionStatus,
        };

        return this.apiService
          .query<UpdateActivationChannelMutation, UpdateActivationChannelMutationVariables>(UpdateActivationChannel, {
            activationChannelInput: activationChannelInput,
          })
          .then((result: any) => {
            if (result.errors?.length) {
              this.nzMessageService.error('Failed to update Activation Channel');
              throw new Error('Failed to update Activation Channel');
            }
            this.triggerOk();
            this.nzMessageService.success('Activation Channel Updated Successfully');
          })
          .catch((error: any) => {
            this.nzMessageService.error(error?.message ?? 'Failed to update Activation Channel');
          });
      }
    } else if (value.channelType === ChannelType.GOOGLE) {
      const { channelSecrets } = value;
      if (channelSecrets) {
        delete value.createdBy;
        const activationChannelInput: UpdateActivationChannelInput = {
          ...value,
          channelId: this.channelId,
          engagement: value.engagement!,
          lastModifiedBy: this.userManagementService.getEmail(),
          connectionStatus: this.connectionStatus,
          channelSecrets: JSON.stringify(channelSecrets),
        };

        return this.apiService
          .query<UpdateActivationChannelMutation, UpdateActivationChannelMutationVariables>(UpdateActivationChannel, {
            activationChannelInput: activationChannelInput,
          })
          .then((result: any) => {
            if (result.errors?.length) {
              this.nzMessageService.error('Failed to update Activation Channel');
              throw new Error('Failed to update Activation Channel');
            }
            this.triggerOk();
            this.nzMessageService.success('Activation Channel Updated Successfully');
          })
          .catch((error: any) => {
            this.nzMessageService.error(error?.message ?? 'Failed to update Activation Channel');
          });
      } else {
        delete value.createdBy;
        const activationChannelInput: UpdateActivationChannelInput = {
          ...value,
          channelId: this.channelId,
          engagement: value.engagement!,
          lastModifiedBy: this.userManagementService.getEmail(),
          connectionStatus: this.connectionStatus,
        };

        return this.apiService
          .query<UpdateActivationChannelMutation, UpdateActivationChannelMutationVariables>(UpdateActivationChannel, {
            activationChannelInput: activationChannelInput,
          })
          .then((result: any) => {
            if (result.errors?.length) {
              this.nzMessageService.error('Failed to update Activation Channel');
              throw new Error('Failed to update Activation Channel');
            }
            this.triggerOk();
            this.nzMessageService.success('Activation Channel Updated Successfully');
          })
          .catch((error: any) => {
            this.nzMessageService.error(error?.message ?? 'Failed to update Activation Channel');
          });
      }
    } else if (value.channelType === ChannelType.S3) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { channelSecrets, createdBy, ...restValue } = value;

      const activationChannelInput: UpdateActivationChannelInput = {
        ...restValue,
        channelId: this.channelId,
        engagement: value.engagement!,
        lastModifiedBy: this.userManagementService.getEmail(),
        connectionStatus: this.connectionStatus,
      };

      return this.apiService
        .query<UpdateActivationChannelMutation, UpdateActivationChannelMutationVariables>(UpdateActivationChannel, {
          activationChannelInput: activationChannelInput,
        })
        .then((result: any) => {
          if (result.errors?.length) {
            this.nzMessageService.error('Failed to update Activation Channel');
            throw new Error('Failed to update Activation Channel');
          }
          this.triggerOk();
          this.nzMessageService.success('Activation Channel Updated Successfully');
        })
        .catch((error: any) => {
          this.nzMessageService.error(error?.message ?? 'Failed to update Activation Channel');
        });
    }

    return Promise.reject('Invalid channel type');
  }

  triggerOk(): void {
    this.nzModalRef.triggerOk();
  }

  resetCredentials(): void {
    this.isResetCredentials = true;
    this.connectionStatus = ConnectionStatus.NOT_VERIFIED;
    this.addSecretsFormGroup();
    if (this.activationChannelForm.get('channelType')?.value === ChannelType.META) {
      this.channelSecretsFormGroup?.reset();
    } else if (this.activationChannelForm.get('channelType')?.value === ChannelType.S3) {
      this.bucketNameFormControl?.reset();
      this.awsRegionFormControl.reset();
      this.iamRoleArnFormControl.reset();
    } else if (this.activationChannelForm.get('channelType')?.value === ChannelType.GOOGLE) {
      this.channelSecretsFormGroup?.reset();
    }
  }

  get isClientIdVerified(): boolean {
    return this.connectionStatus === ConnectionStatus.SUCCESS;
  }

  get activationChannelNameFormControl(): UntypedFormControl {
    return this.activationChannelForm.get('name') as UntypedFormControl;
  }

  get channelSecretsFormGroup(): UntypedFormControl {
    return this.activationChannelForm.get('channelSecrets') as UntypedFormControl;
  }

  get appIdFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('appId') as UntypedFormControl;
  }

  get appSecretFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('appSecret') as UntypedFormControl;
  }

  get appAccessTokenFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('appAccessToken') as UntypedFormControl;
  }

  get adAccountIdFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('adAccountId') as UntypedFormControl;
  }

  get awsRegionFormControl(): UntypedFormControl {
    return this.activationChannelForm.get('awsRegion') as UntypedFormControl;
  }

  get bucketNameFormControl(): UntypedFormControl {
    return this.activationChannelForm.get('bucketName') as UntypedFormControl;
  }

  get iamRoleArnFormControl(): UntypedFormControl {
    return this.activationChannelForm.get('iamRoleArn') as UntypedFormControl;
  }

  get channelTypeFormControl(): UntypedFormControl {
    return this.activationChannelForm.get('channelType') as UntypedFormControl;
  }

  get oauthClientIdFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('oauthClientId') as UntypedFormControl;
  }

  get oauthClientSecretFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('oauthClientSecret') as UntypedFormControl;
  }

  get refreshTokenFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('refreshToken') as UntypedFormControl;
  }

  get developerTokenFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('developerToken') as UntypedFormControl;
  }

  get customerIdFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('customerId') as UntypedFormControl;
  }

  get loginCustomerIdFormControl(): UntypedFormControl {
    return this.channelSecretsFormGroup.get('loginCustomerId') as UntypedFormControl;
  }
}
